import { CurrentFlow } from 'enums/CurrentFlow';
import { RootState } from 'handlers';
import { getClientTimezone } from 'utils/dateUtils';
import { resolveEligibleCredential } from 'utils/credentialUtils';

export const getLoanOffer = ({ loanOffer }: RootState) => loanOffer;
export const getLoanOfferData = ({ loanOffer }: RootState) => loanOffer.response;

export const getLoanOfferPayload = ({
  nursegridData: { nursegridUserId },
  yourGoal: { loanGoal, aiCoachGoals },
  yourIncome: {
    not_employed: notEmployed,
    total_annual_income: totalAnnualIncome,
    start_of_employment: startOfEmployment,
    employer_name: employerName,
    job_title: jobTitle,
    pay_frequency: payFrequency,
    credential_acquisition_date: credentialsAcquisitionDate,
    additional_credential: additionalCredential,
    no_other_credentials: noOtherCredentials,
    additional_credential_acquisition_date: additionalCredentialAcquisitionDate,
  },
  yourEmployer: { additionalIncome },
  yourContact: { email, phone_number: phoneNumber },
  yourName: { first_name: firstName, last_name: lastName, credentials },
  professionGroup: { professionGroup },
  preQualificationData: { partnerId, promoCode },
  auth: { sessionToken, hstreamUserInfo: hStreamAuthData },
  applicationStep: { currentFlow },
  cardData: { borrowerCredentials: borrowerCardCredentials },
  yourAddress: { address_line_1: streetAddress, city, zip_code: zipCode, argyle_state: state },
  yourBirthDate: { birth_date: dateOfBirth },
  yourSSN4: { SSN4: last4SSN },
  applicationData: { application },
  reapplyData: { isReapplying, updatedFields },
}: RootState) => {
  const { credentials: resolvedCredentials, acquisitionDate: resolvedAcquisitionDate } = resolveEligibleCredential({
    currentCredentials: credentials,
    currentAcquisitionDate: credentialsAcquisitionDate,
    additionalCredential,
    additionalAcquisitionDate: additionalCredentialAcquisitionDate,
    hasNoOtherCredentials: Boolean(noOtherCredentials),
  });

  const shared = {
    nursegridUserId,
    timezone: getClientTimezone(),
    currentFlow,
    sessionToken,
    isReapplying,
    promoCode,
    ...(hStreamAuthData && {
      authData: {
        hStreamAuthData,
      },
    }),
  };

  if (isReapplying) {
    return {
      ...shared,
      currentFlow: CurrentFlow.DebtConsolidation,
      application: {
        professionGroup,
        resumeLink: `${window.location.href}`,
        partnerId,
        loanGoal: loanGoal ?? '[]',
        ...(updatedFields.credentialsAcquisitionDate && {
          credentialsAcquisitionDate: updatedFields.credentialsAcquisitionDate,
        }),
        ...(updatedFields.totalAnnualIncome != null && {
          totalAnnualIncome: updatedFields.totalAnnualIncome,
        }),
      },
      borrower: {
        credentials: resolvedCredentials ?? borrowerCardCredentials ?? application?.borrowerCredentials,
        firstName: firstName || application?.borrowerFirstName || '',
        lastName: lastName || application?.borrowerLastName || '',
        ...(phoneNumber && { phoneNumber }),
        ...(updatedFields.email && { email: updatedFields.email }),
        ...(updatedFields.address && {
          streetAddress: updatedFields.address.street,
          city: updatedFields.address.city,
          zipCode: updatedFields.address.zipOrPostalCode,
          state: updatedFields.address.stateOrProvince,
        }),
      },
      ...(updatedFields.notEmployed != null && { notEmployed: updatedFields.notEmployed }),
      ...(updatedFields.notEmployed === false &&
        updatedFields.primaryEmployerName && {
          employment: [{ employerName: updatedFields.primaryEmployerName }],
        }),
      ...(updatedFields.additionalIncome != null && {
        yourEmployer: { additionalIncome: updatedFields.additionalIncome },
      }),
    };
  }

  return {
    ...shared,
    application: {
      professionGroup,
      totalAnnualIncome: totalAnnualIncome ?? 0,
      resumeLink: `${window.location.href}`,
      partnerId,
      loanGoal: loanGoal ?? '[]',
      ...(aiCoachGoals && aiCoachGoals.length > 0 && { aiCoachGoals }),
      credentialsAcquisitionDate: resolvedAcquisitionDate,
    },
    borrower: {
      credentials: resolvedCredentials ?? borrowerCardCredentials,
      streetAddress,
      city,
      zipCode,
      state,
      dateOfBirth,
      firstName,
      lastName,
      phoneNumber,
      email,
      last4SSN,
    },
    notEmployed,
    employment: [
      {
        employerName,
        jobTitle,
        hireDate: startOfEmployment ?? undefined,
        payFrequency,
      },
    ],
    ...(additionalIncome != null && { yourEmployer: { additionalIncome } }),
  };
};
